import type { MutableRefObject } from 'react';
import React, { useEffect, useState } from 'react';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { Drawer } from '@noths/polaris-client-ribbons-design-system';
import { getHashFromURL, once } from '@noths/polaris-client-utils';

import { DeliveryFAQs } from 'src/components/molecules/DeliveryFAQs/DeliveryFAQs';
import { DeliveryInfoContainer } from 'src/components/molecules/DeliveryInfo/DeliveryInfoContainer';
import { DeliveryBlockContainer } from 'src/components/organisms/DeliveryBlock/DeliveryBlockContainer';
import { DELIVERY_DRAWER_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import * as copy from './copy';
import * as styles from './DeliveryDrawer.styles';

interface DeliveryDrawerProps {
  target: MutableRefObject<HTMLDivElement | null>;
}

const isMobileDevice = () => {
  return /Mobi|Android/i.test(navigator.userAgent);
};

const sendViewContentEventOnce = once(() => {
  sendGAEvent({
    event: 'custom_event_v2',
    event_name: 'view_content',
    event_details: undefined,
    content_type: 'delivery information',
    item_id: undefined,
  });
});

export const DeliveryDrawer = ({ target }: DeliveryDrawerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [width, setWidth] = useState(0);

  const handleHashChange = ({ newURL }: Pick<HashChangeEvent, 'newURL'>) => {
    const hashIsDeliveryDrawer = getHashFromURL(newURL) === DELIVERY_DRAWER_URL_FRAGMENT_ID;

    setIsOpen(hashIsDeliveryDrawer);

    if (hashIsDeliveryDrawer) {
      sendViewContentEventOnce();
    }
  };

  const clearURLHash = () => {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    handleHashChange({ newURL: window.location.href });
  };

  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);

    handleHashChange({ newURL: window.location.href });

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      const leftPos = target.current?.getBoundingClientRect().left;
      const currentWidth = isMobileDevice()
        ? window.screen.width - leftPos!
        : window.innerWidth - leftPos!;
      setWidth(currentWidth);
    };

    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Drawer
      contentLabel={copy.DRAWER_CONTENT_LABEL}
      describedBy={copy.DRAWER_DESCRIPTOR_ID}
      isOpen={isOpen}
      onRequestClose={clearURLHash}
    >
      <section css={styles.section} style={{ width }}>
        <header css={styles.header}>
          <button
            aria-expanded={isOpen}
            aria-label="Close"
            css={styles.closeButton}
            onClick={clearURLHash}
          />
        </header>
        <div css={styles.content}>
          <DeliveryBlockContainer />
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          <p css={styles.message}>You'll pick your delivery options at checkout</p>
          <DeliveryFAQs />
          <DeliveryInfoContainer />
        </div>
      </section>
    </Drawer>
  );
};
